import { Slot } from '@radix-ui/react-slot';
import { forwardRef } from 'react';

import { Icons, type LBIcon } from '@/assets/icons/icons';
import { cn } from '@/lib/utils';

type Size = 'default' | 'lg';

type RootProps = {
  asChild?: boolean;
  size?: Size;
  selected?: boolean;
  muted?: boolean;
  interactive?: boolean;
} & React.HTMLAttributes<HTMLDivElement>;

const Root = forwardRef<HTMLDivElement, RootProps>(
  (
    {
      asChild,
      size = 'default',
      selected,
      muted,
      interactive = true,
      className,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : 'div';
    return (
      <Comp
        ref={ref}
        className={cn(
          'flex w-full min-w-0 items-center gap-2 rounded-xl px-[var(--row-pad,8px)] text-left text-[0.875rem]',
          size === 'lg' ? 'h-9' : 'h-[30px]',
          muted ? 'text-foreground-muted' : 'text-foreground-subtle',
          interactive &&
            'transition-colors hover:bg-surface-hover hover:text-foreground focus-ring-inset data-[state=open]:bg-surface-hover',
          selected &&
            'bg-surface-active text-foreground hover:bg-surface-active',
          className
        )}
        {...props}
      />
    );
  }
);
Root.displayName = 'SidebarRow.Root';

const UNREAD_CUTOUT_MASK =
  'radial-gradient(circle at calc(100% - 0.1875rem) calc(100% - 0.1875rem), transparent 0.3125rem, #000 calc(0.3125rem + 0.5px))';

const UNREAD_COLOR = 'bg-brand-jordy-blue';

const Leading = ({
  size = 'default',
  unread,
  className,
  children,
}: {
  size?: Size;
  unread?: boolean;
  className?: string;
  children?: React.ReactNode;
}) => (
  <span
    className={cn(
      'relative inline-flex shrink-0 items-center justify-center text-foreground-muted',
      size === 'lg' ? 'size-6' : 'size-[18px]',
      className
    )}
  >
    <span
      className="inline-flex size-full items-center justify-center"
      style={
        unread
          ? {
              maskImage: UNREAD_CUTOUT_MASK,
              WebkitMaskImage: UNREAD_CUTOUT_MASK,
            }
          : undefined
      }
    >
      {children}
    </span>
    {unread && (
      <span
        role="status"
        aria-label="Unread"
        className={cn(
          'absolute bottom-0 right-0 size-1.5 rounded-full',
          UNREAD_COLOR
        )}
      />
    )}
  </span>
);

const Icon = ({
  icon: IconComponent,
  className,
  children,
}: {
  icon?: LBIcon;
  className?: string;
  children?: React.ReactNode;
}) => (
  <span
    className={cn(
      'inline-flex size-3.5 shrink-0 items-center justify-center [&>svg]:size-full',
      className
    )}
  >
    {IconComponent ? <IconComponent /> : children}
  </span>
);

const Label = ({
  className,
  children,
}: {
  className?: string;
  children?: React.ReactNode;
}) => (
  <span className={cn('min-w-0 flex-1 truncate', className)}>{children}</span>
);

const StatusDot = ({ state }: { state: 'idle' | 'busy' | 'unread' }) => (
  <span aria-hidden className="relative flex size-1.5 shrink-0">
    <span
      className={cn(
        'relative size-full rounded-full',
        state === 'unread' && UNREAD_COLOR,
        state === 'busy' && 'animate-pulse bg-foreground-muted',
        state === 'idle' && 'bg-foreground-muted opacity-25'
      )}
    />
  </span>
);

const AddIcon = () => (
  <span className="flex size-full items-center justify-center rounded-full bg-secondary-active text-foreground-subtle">
    <Icons.plus className="size-1/2" strokeWidth={2} />
  </span>
);

type ButtonProps = {
  size?: Size;
  leading?: React.ReactNode;
  trailing?: React.ReactNode;
  selected?: boolean;
  muted?: boolean;
} & React.ButtonHTMLAttributes<HTMLButtonElement>;

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ size, leading, trailing, selected, muted, children, ...props }, ref) => (
    <Root asChild size={size} selected={selected} muted={muted}>
      <button ref={ref} type="button" {...props}>
        <Leading size={size}>{leading}</Leading>
        <Label>{children}</Label>
        {trailing}
      </button>
    </Root>
  )
);
Button.displayName = 'SidebarRow.Button';

export const SidebarRow = {
  Root,
  Leading,
  Icon,
  Label,
  StatusDot,
  AddIcon,
  Button,
};
